import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import Logo from "./Logo";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    background: isScrolled
      ? 'rgba(255, 255, 255, 0.95)'
      : 'linear-gradient(135deg, var(--white) 0%, var(--gray-50) 100%)',
    backdropFilter: isScrolled ? 'blur(10px)' : 'none',
    padding: isScrolled ? '0.75rem 0' : '1rem 0',
    transition: 'all 0.3s ease',
    zIndex: 1000,
    boxShadow: isScrolled ? 'var(--shadow-lg)' : 'none',
    borderBottom: isScrolled ? '1px solid rgba(123, 176, 98, 0.3)' : '1px solid rgba(0, 0, 0, 0.1)'
  };

  const containerStyle = {
    maxWidth: '1200px',
    margin: 'auto',
    padding: '0 1rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  };

  const navLinksStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '2rem',
    listStyle: 'none'
  };

  const mobileNavLinksStyle = {
    ...navLinksStyle,
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    background: 'var(--white)',
    flexDirection: 'column',
    gap: '0',
    padding: '1rem 0',
    transform: isMobileMenuOpen ? 'translateY(0)' : 'translateY(-100%)',
    opacity: isMobileMenuOpen ? 1 : 0,
    visibility: isMobileMenuOpen ? 'visible' : 'hidden',
    transition: 'all 0.3s ease',
    boxShadow: 'var(--shadow-lg)'
  };

  const linkStyle = {
    color: 'var(--gray-700)',
    textDecoration: 'none',
    fontWeight: 500,
    fontSize: '1rem',
    padding: '0.5rem 1rem',
    borderRadius: 'var(--radius-md)',
    transition: 'all 0.3s ease',
    position: 'relative',
    overflow: 'hidden'
  };

  const activeLinkStyle = {
    ...linkStyle,
    background: 'var(--primary-green)',
    color: 'white'
  };

  const mobileLinkStyle = {
    ...linkStyle,
    width: '100%',
    textAlign: 'center',
    padding: '1rem',
    borderBottom: '1px solid var(--gray-200)',
    color: 'var(--gray-700)'
  };

  const mobileMenuButtonStyle = {
    display: 'none',
    background: 'transparent',
    border: 'none',
    color: 'var(--gray-700)',
    fontSize: '1.5rem',
    cursor: 'pointer',
    padding: '0.5rem'
  };

  const hamburgerStyle = {
    width: '24px',
    height: '20px',
    position: 'relative',
    cursor: 'pointer'
  };

  const hamburgerLineStyle = (index) => ({
    display: 'block',
    position: 'absolute',
    height: '2px',
    width: '100%',
    background: 'var(--gray-700)',
    borderRadius: '1px',
    opacity: 1,
    left: 0,
    transform: isMobileMenuOpen
      ? index === 0 ? 'rotate(45deg) translate(5px, 5px)'
        : index === 1 ? 'opacity(0)'
          : 'rotate(-45deg) translate(7px, -6px)'
      : 'none',
    top: index === 0 ? '0px' : index === 1 ? '9px' : '18px',
    transition: 'all 0.3s ease'
  });

  const isActive = (path) => location.pathname === path;

  return (
    <nav style={navStyle}>
      <div style={containerStyle}>
        <Link to="/" style={{ textDecoration: 'none' }}>
          <Logo size="small" />
        </Link>

        {/* Desktop Navigation */}
        <ul style={{ ...navLinksStyle, display: window.innerWidth > 768 ? 'flex' : 'none' }}>
          <li>
            <Link
              to="/"
              style={isActive('/') ? activeLinkStyle : linkStyle}
              onMouseEnter={(e) => {
                if (!isActive('/')) {
                  e.target.style.background = 'var(--gray-100)';
                  e.target.style.transform = 'translateY(-2px)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive('/')) {
                  e.target.style.background = 'transparent';
                  e.target.style.transform = 'translateY(0)';
                }
              }}
            >
              Home
            </Link>
          </li>
          <li>
            <Link
              to="/about"
              style={isActive('/about') ? activeLinkStyle : linkStyle}
              onMouseEnter={(e) => {
                if (!isActive('/about')) {
                  e.target.style.background = 'var(--gray-100)';
                  e.target.style.transform = 'translateY(-2px)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive('/about')) {
                  e.target.style.background = 'transparent';
                  e.target.style.transform = 'translateY(0)';
                }
              }}
            >
              About
            </Link>
          </li>
          <li>
            <Link
              to="/solutions"
              style={isActive('/solutions') ? activeLinkStyle : linkStyle}
              onMouseEnter={(e) => {
                if (!isActive('/solutions')) {
                  e.target.style.background = 'var(--gray-100)';
                  e.target.style.transform = 'translateY(-2px)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive('/solutions')) {
                  e.target.style.background = 'transparent';
                  e.target.style.transform = 'translateY(0)';
                }
              }}
            >
              Solutions
            </Link>
          </li>
          <li>
            <Link
              to="/why-us"
              style={isActive('/why-us') ? activeLinkStyle : linkStyle}
              onMouseEnter={(e) => {
                if (!isActive('/why-us')) {
                  e.target.style.background = 'var(--gray-100)';
                  e.target.style.transform = 'translateY(-2px)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive('/why-us')) {
                  e.target.style.background = 'transparent';
                  e.target.style.transform = 'translateY(0)';
                }
              }}
            >
              Why Choose Us
            </Link>
          </li>
          <li>
            <Link
              to="/contact"
              className="btn btn-accent"
              style={{
                background: 'linear-gradient(135deg, var(--primary-green) 0%, var(--accent-green-bright) 100%)',
                color: 'white',
                padding: '0.75rem 1.5rem',
                borderRadius: 'var(--radius-md)',
                textDecoration: 'none',
                fontWeight: 600,
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = 'var(--shadow-lg)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }}
            >
              Contact
            </Link>
          </li>
        </ul>

        {/* Mobile Menu Button */}
        <button
          style={{ ...mobileMenuButtonStyle, display: window.innerWidth <= 768 ? 'block' : 'none' }}
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          <div style={hamburgerStyle}>
            <span style={hamburgerLineStyle(0)}></span>
            <span style={hamburgerLineStyle(1)}></span>
            <span style={hamburgerLineStyle(2)}></span>
          </div>
        </button>

        {/* Mobile Navigation */}
        <ul style={{ ...mobileNavLinksStyle, display: window.innerWidth <= 768 ? 'flex' : 'none' }}>
          <li><Link to="/" style={mobileLinkStyle} onClick={() => setIsMobileMenuOpen(false)}>Home</Link></li>
          <li><Link to="/about" style={mobileLinkStyle} onClick={() => setIsMobileMenuOpen(false)}>About</Link></li>
          <li><Link to="/solutions" style={mobileLinkStyle} onClick={() => setIsMobileMenuOpen(false)}>Solutions</Link></li>
          <li><Link to="/why-us" style={mobileLinkStyle} onClick={() => setIsMobileMenuOpen(false)}>Why Choose Us</Link></li>
          <li><Link to="/contact" style={mobileLinkStyle} onClick={() => setIsMobileMenuOpen(false)}>Contact</Link></li>
        </ul>
      </div>
    </nav>
  );
};

export default Navbar;